<div class="container-fluid px-5 py-3">
    <div class="d-flex justify-content-between pb-2">
        <span class="text-blue font-weight-bold">
            <h3 class="my-2" translate>listBankStatement.title</h3>
        </span>

        <!-- Manual runs of the two nightly jobs, for recovering from a night that failed
             without waiting for the next one. Both disable on `working` rather than only
             the pressed one: they act on the same rows, and starting the matcher while the
             import is still bringing statements in would only half do the job. -->
        <div class="d-flex align-items-center" *ngIf="perms.hasAccess(perms.CAN_TRIGGER_BANK_STATEMENT_JOBS)">
            <span *ngIf="working" class="spinner-border spinner-border-sm text-primary mr-2" role="status" aria-hidden="true"></span>

            <button class="btn btn-outline-primary" type="button" [disabled]="working"
                data-toggle="tooltip" data-placement="bottom"
                title="{{'listBankStatement.importRecentHint' | translate}}"
                (click)="importRecent()">
                <i class="fa fa-download mr-1"></i><span translate>listBankStatement.importRecent</span>
            </button>

            <button class="btn btn-outline-primary ml-2" type="button" [disabled]="working"
                data-toggle="tooltip" data-placement="bottom"
                title="{{'listBankStatement.rematchRecentHint' | translate}}"
                (click)="rematchRecent()">
                <i class="fa fa-link mr-1"></i><span translate>listBankStatement.rematchRecent</span>
            </button>
        </div>
    </div>

    <div class="my-2 py-2">
        <!-- Search Form -->
        <form [formGroup]="searchForm">
            <div class="input-group mb-2">
                <!-- match status -->
                <div class="input-group-prepend">
                    <span class="input-group-text" translate>listBankStatement.matchStatus</span>
                </div>
                <select class="custom-select input-custom-size" formControlName="matchStatus">
                    <option [ngValue]="null" translate>placeholder.select</option>
                    <option *ngFor="let status of matchStatuses" [value]="status" translate>{{matchStatusKey(status)}}</option>
                </select>

                <!-- reference -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" translate>listBankStatement.reference</span>
                </div>
                <input type="text" class="form-control d-inline" (keyup.enter)="search()" formControlName="reference" appTrimWhitespace>

                <!-- payment details free text -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" translate>listBankStatement.paymentDetails</span>
                </div>
                <input type="text" class="form-control d-inline" (keyup.enter)="search()" formControlName="paymentDetails" appTrimWhitespace>
            </div>

            <div class="input-group mb-2">
                <!-- invoice number of a linked document -->
                <div class="input-group-prepend">
                    <span class="input-group-text" translate>listBankStatement.invoiceNumber</span>
                </div>
                <input type="text" class="form-control d-inline" (keyup.enter)="search()" formControlName="invoiceNumber" appTrimWhitespace>

                <!-- amount -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" translate>listBankStatement.amount</span>
                </div>
                <input type="number" step="0.01" class="form-control d-inline" style="max-width: 150pt;"
                    (keyup.enter)="search()" formControlName="amount">

                <!-- наредител -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" translate>listBankStatement.beneficiaryBankName</span>
                </div>
                <input type="text" class="form-control d-inline" (keyup.enter)="search()"
                    formControlName="beneficiaryBankName" appTrimWhitespace>

                <!-- payment type: the bank's own wording, already in Bulgarian, so shown
                     verbatim rather than through a translation key -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" translate>listBankStatement.paymentType</span>
                </div>
                <select class="custom-select input-custom-size" formControlName="paymentType">
                    <option [ngValue]="null" translate>placeholder.select</option>
                    <option *ngFor="let paymentType of paymentTypes" [value]="paymentType">{{paymentType}}</option>
                </select>
            </div>

            <div class="input-group mb-2">
                <!-- date from -->
                <div class="input-group-prepend">
                    <span class="input-group-text" id="dateFrom" translate>listBankStatement.dateFrom</span>
                </div>
                <input class="form-control d-inline" style="max-width: 200pt;" aria-describedby="dateFrom"
                    formControlName="dateFrom" ngbDatepicker #d1="ngbDatepicker" readonly>
                <div class="input-group-append">
                    <button class="btn btn-outline-secondary fa fa-calendar" (click)="d1.toggle()" type="button"></button>
                </div>

                <!-- date to -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" id="dateTo" translate>listBankStatement.dateTo</span>
                </div>
                <input class="form-control d-inline" style="max-width: 200pt;" aria-describedby="dateTo"
                    formControlName="dateTo" ngbDatepicker #d2="ngbDatepicker" readonly>
                <div class="input-group-append">
                    <button class="btn btn-outline-secondary fa fa-calendar" (click)="d2.toggle()" type="button"></button>
                </div>

                <!-- actions -->
                <div class="input-group-prepend ml-2">
                    <div style="display: flex;">
                        <button class="btn btn-outline-success" style="border-radius: 0;" type="button" (click)="search()" translate>button.search</button>
                        <button class="btn btn-outline-danger ml-2" style="border-radius: 0;" type="button" (click)="clearSearch()" translate>button.clear</button>

                        <!-- Exports what the filters select, not just the page on screen. -->
                        <i style="margin-left: 10px; margin-top: 4px" class="fa fa-file-excel-o fa-2x"
                            data-toggle="tooltip" data-placement="bottom" title="{{'button.excel' | translate}}"
                            (click)="exportExcel()"></i>
                    </div>
                </div>
            </div>
        </form>

        <ng-container *ngIf="!loadExcel; else dataLoadingTemplate"></ng-container>

        <br>

        <!-- Table -->
        <div class="table-responsive">
            <table class="table table-bordered table-striped w-100" [hidden]="!dataLoaded">
                <thead>
                    <tr>
                        <th sortBy="reference" (sort)="onSort($event)" scope="col" translate>listBankStatement.reference</th>
                        <th sortBy="transactionDate" (sort)="onSort($event)" scope="col" translate>listBankStatement.transactionDate</th>
                        <th sortBy="amount" (sort)="onSort($event)" scope="col" class="text-right" translate>listBankStatement.amount</th>
                        <th sortBy="paymentType" (sort)="onSort($event)" scope="col" translate>listBankStatement.paymentType</th>
                        <th scope="col" translate>listBankStatement.paymentDetails</th>
                        <th scope="col" translate>listBankStatement.beneficiaryBankName</th>
                        <th sortBy="matchStatus" (sort)="onSort($event)" scope="col" translate>listBankStatement.matchStatus</th>
                        <th scope="col" translate>listBankStatement.matchNote</th>
                        <th scope="col" translate>listBankStatement.matchedInvoiceNumbers</th>
                        <th scope="col" class="text-center" translate>listBankStatement.actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let statement of content">
                        <td>{{statement.reference}}</td>
                        <td>{{statement.transactionDate}}</td>
                        <td class="text-right text-nowrap">{{statement.amount | number: '1.2-2'}} {{statement.currency}}</td>
                        <td>{{statement.paymentType}}</td>
                        <td>{{statement.paymentDetails}}</td>
                        <td>{{statement.beneficiaryBankName}}</td>
                        <td>
                            <span [ngClass]="matchStatusClass(statement.matchStatus)" translate>{{matchStatusKey(statement.matchStatus)}}</span>
                        </td>
                        <td>
                            {{statement.matchNote}}
                            <!-- Shown under the note rather than in a column of its own: only a
                                 written off row carries one, and it is the reason for the status
                                 beside it, so it belongs with the other explanation of the row. -->
                            <div *ngIf="statement.matchErrorText" class="small text-danger mt-1">
                                <i class="fa fa-exclamation-triangle mr-1"></i>{{statement.matchErrorText}}
                            </div>
                        </td>
                        <td>{{statement.matchedInvoiceNumbers}}</td>
                        <td class="text-center py-2">
                            <button class="btn btn-sm btn-success" type="button"
                                *ngIf="perms.hasAccess(perms.CAN_MATCH_BANK_STATEMENT)"
                                data-toggle="tooltip" data-placement="bottom" title="{{'listBankStatement.match' | translate}}"
                                (click)="openMatchDialog(statement)">
                                <i class="fa fa-link fa-lg"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <ng-container *ngIf="dataLoaded; else dataLoadingTemplate"></ng-container>

        <!-- Pagination -->
        <div class="paging-wrapper d-flex justify-content-between">
            <select class="pagination-size custom-select" [(ngModel)]="sortingPaging.pageSize" [ngModelOptions]="{standalone: true}" (change)="onPageSizeChange($event)">
                <option *ngFor="let item of pageSizeOptions; let i = index" [value]="pageSizeOptions[i]">
                    {{pageSizeOptions[i]}} {{'paging.itemsPerPage' | translate}}
                </option>
            </select>
            <ngb-pagination class="pagination-control"
                [(page)]="sortingPaging.pageNumber"
                [pageSize]="sortingPaging.pageSize"
                [collectionSize]="sortingPaging.totalElements"
                [boundaryLinks]="true"
                [maxSize]="maxSize"
                (pageChange)="pageChanged($event)">
            </ngb-pagination>
            <span class="pagination-index border center">
                {{sortingPaging.fromRow}} - {{sortingPaging.toRow}} / {{sortingPaging.totalElements}}
            </span>
        </div>
    </div>
</div>

<ng-template #dataLoadingTemplate>
    <div *ngIf="errorMessage$ | async as errorMessage; else dataSpinner">
        <div class="alert alert-danger">
            {{errorMessage}}
        </div>
        <div class="d-flex justify-content-center">
            <i class="fa fa-refresh" (click)="reload()"></i>
        </div>
    </div>
    <ng-template #dataSpinner>
        <div class="d-flex justify-content-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </ng-template>
</ng-template>
